import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { useAuth } from './useAuth.js'

/**
 * The store's coupon codes, with what each one has done.
 *
 * Rows, not a settings object — created, toggled and deleted one at a time —
 * so every write refreshes the list rather than patching it locally: the
 * server decides the status and the visible count.
 */

export type CouponStatus = 'live' | 'off' | 'scheduled' | 'expired' | 'used_up'

export interface Coupon {
  id: string
  code: string
  description: string
  type: 'percent' | 'fixed'
  value: number
  label: string
  minSubtotal: number
  maxDiscount: number | null
  startsAt: string | null
  endsAt: string | null
  usageLimit: number | null
  perCustomerLimit: number | null
  usedCount: number
  active: boolean
  showAtCheckout: boolean
  firstOrderOnly: boolean
  status: CouponStatus
  orders: number
  revenue: number
  discountGiven: number
  createdAt: string
}

export interface CouponList {
  coupons: Coupon[]
  visible: number
  visibleLimit: number
}

/** What the form posts. */
export type CouponDraft = Pick<
  Coupon,
  | 'code'
  | 'description'
  | 'type'
  | 'value'
  | 'minSubtotal'
  | 'maxDiscount'
  | 'startsAt'
  | 'endsAt'
  | 'usageLimit'
  | 'perCustomerLimit'
  | 'active'
  | 'showAtCheckout'
  | 'firstOrderOnly'
>

export function useDiscounts() {
  const { shop } = useAuth()
  const queryClient = useQueryClient()

  const key = ['discounts', shop?.id]
  const refresh = (): void => void queryClient.invalidateQueries({ queryKey: key })

  const query = useQuery({
    queryKey: key,
    queryFn: () => api<CouponList>('/v1/discounts'),
    enabled: Boolean(shop?.id),
  })

  const create = useMutation({
    mutationFn: (body: CouponDraft) => api<Coupon>('/v1/discounts', { method: 'POST', body }),
    onSuccess: refresh,
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CouponDraft }) =>
      api<Coupon>(`/v1/discounts/${id}`, { method: 'PUT', body }),
    onSuccess: refresh,
  })

  const toggle = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { active?: boolean; showAtCheckout?: boolean } }) =>
      api<Coupon>(`/v1/discounts/${id}`, { method: 'PATCH', body }),
    onSuccess: refresh,
  })

  const duplicate = useMutation({
    mutationFn: (id: string) => api<Coupon>(`/v1/discounts/${id}/duplicate`, { method: 'POST' }),
    onSuccess: refresh,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api<{ deleted: boolean }>(`/v1/discounts/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  })

  return {
    shop,
    list: query.data ?? null,
    isPending: query.isPending,
    error: query.error,
    create,
    update,
    toggle,
    duplicate,
    remove,
  }
}
